import { useState } from "react";
import { Star } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";

export function SessionRating({
  attendanceId,
  bookingId,
  initialRating,
  initialNote,
  onSubmit,
}: {
  attendanceId?: string | null;
  bookingId: string;
  initialRating?: number;
  initialNote?: string;
  onSubmit: (rating: number, note: string, attendanceId: string | null) => void;
}) {
  const { t } = useTranslation(["common"]);
  const [rating, setRating] = useState(initialRating ?? 0);
  const [note, setNote] = useState(initialNote ?? "");
  const [loading, setLoading] = useState(false);

  const handleRate = (val: number) => setRating(val);

  const handleSubmit = async () => {
    setLoading(true);
    try {
      // `onSubmit` peut rejeter (échec d'enregistrement) : sans `finally`, le
      // `setLoading(false)` n'était jamais atteint et le bouton restait bloqué
      // sur « Envoi... », la note perdue sans message.
      await onSubmit(rating, note, attendanceId ?? null);
    } catch (error) {
      console.error('[SessionRating] submit failed', error);
      toast.error(t("ratingSaveError", { ns: "common" }));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex flex-col gap-2 w-full max-w-md mx-auto bg-muted/60 p-3 rounded-xl">
      <div className="flex justify-center gap-1">
        {[1, 2, 3, 4, 5].map((val) => (
          <button
            key={val}
            type="button"
            className={`focus:outline-none transition-colors ${val <= rating ? "text-yellow-400" : "text-gray-400"}`}
            onClick={() => handleRate(val)}
            disabled={loading}
            aria-label={t("ratingAria", { ns: "common", value: val })}
          >
            <Star className={`h-7 w-7 ${val <= rating ? "fill-yellow-400" : ""}`} />
          </button>
        ))}
      </div>
      <Textarea
        rows={1}
        placeholder={t("ratingPlaceholder", { ns: "common" })}
        value={note}
        onChange={e => setNote(e.target.value)}
        disabled={loading}
        className="w-full min-w-0 resize-none"
        style={{ maxWidth: 350 }}
      />
      <div className="flex justify-end">
        <Button
          size="sm"
          className="mt-1"
          disabled={loading || rating < 1}
          onClick={handleSubmit}
        >
          {loading ? "Envoi..." : "Envoyer"}
        </Button>
      </div>
    </div>
  );
}
